import { type FC, useCallback, useState } from 'react';

import { addFavoriteGameRequest, removeFavoriteGameRequest } from 'api/casino';
import Icon from 'components/Icon/Icon';
import { useCasinoGames } from 'pages/CasinoPage/casino.context';
import { AuthStatus, useClientProfile, useNotifications } from 'store';
import { AUTH_PAGES, useAuthRedirects } from 'utils/authRedirects';

import styles from './FavoriteButton.module.scss';

type Props = {
  gameId: number;
};

const FavoriteButton: FC<Props> = ({ gameId }) => {
  const { redirectToAuthPage } = useAuthRedirects();
  const [{ add }] = useNotifications();
  const [disabled, setDisabled] = useState(false);
  const { addFavoriteGame, deleteFavoriteGame, favoriteGames } =
    useCasinoGames();
  const [{ sessionToken, status }] = useClientProfile();
  const isFavorite = favoriteGames.has(gameId);

  const onRemoveFavorite = useCallback(() => {
    setDisabled(true);
    removeFavoriteGameRequest({
      body: { gameId },
      headers: { authorization: `Bearer ${sessionToken}` },
    })
      .then(() => {
        deleteFavoriteGame(gameId);
      })
      .catch(() => {
        add({ text: 'something_went_wrong' });
      })
      .finally(() => {
        setDisabled(false);
      });
  }, [sessionToken, deleteFavoriteGame, gameId, add]);

  const onAddFavorite = useCallback(() => {
    setDisabled(true);
    addFavoriteGameRequest({
      body: { gameId },
      headers: { authorization: `Bearer ${sessionToken}` },
    })
      .then(() => {
        addFavoriteGame(gameId);
      })
      .catch(() => {
        add({ text: 'something_went_wrong' });
      })
      .finally(() => {
        setDisabled(false);
      });
  }, [sessionToken, addFavoriteGame, gameId, add]);

  const favoriteButtonHandler = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (status === AuthStatus.signedOut) {
        redirectToAuthPage(AUTH_PAGES.signIn, { gameId });
        return;
      }

      if (isFavorite) {
        onRemoveFavorite();
        return;
      }

      onAddFavorite();
    },
    [
      isFavorite,
      onRemoveFavorite,
      onAddFavorite,
      status,
      redirectToAuthPage,
      gameId,
    ],
  );

  return (
    <button
      className={styles['favorite-button']}
      type="button"
      onClick={favoriteButtonHandler}
      disabled={disabled}
    >
      <Icon name={isFavorite ? 'star' : 'star-outlined'} />
    </button>
  );
};

export default FavoriteButton;
